import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { forkJoin, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { DeletePopupComponent } from "src/app/shared/components/delete-popup/delete-popup.component";
import { hasValue } from "src/app/shared/helpers/filter-utils";
import { AssessmentCentralService } from "src/app/shared/services/assessment-central.service";
import { FilterService } from "src/app/shared/services/filter.service";
import { MessageService } from "src/app/shared/services/message.service";
import { TagsService } from "src/app/shared/services/tag.service";
import { AssessmentCreateTemplateModalDialogComponent } from "../assessment-template-create-modal/assessment-template-create-modal";
import { AssessmentCreateModalDialogComponent } from "./assessment-create-modal/assessment-create-modal";
import { LoggingService } from "src/app/logging.service";
import { AuthService } from "src/app/shared/services/auth.service";
import { CreateSurveyDialogComponent } from "../../pulse/create-survey-dialog/create-survey-dialog.component";
import { BreadcrumbService } from "src/app/shared/services/breadcrumb.service";
import { CommonService } from "src/app/shared/services/common.service";

@Component({
  selector: "assessment-preview-modal",
  templateUrl: "assessment-preview-modal.html",
  styleUrls: ["assessment-preview-modal.scss"],
})
export class AssessmentPreviewModalDialogComponent implements OnInit {
  template: any;
  questions: any = [];
  tagsList: any;
  category = "";
  description: any;
  applicability: any;
  assessmentScoringScale: any;
  categoryObject: any;
  categoriesList: any;
  showTags = false;

  constructor(
    public dialogRef: MatDialogRef<AssessmentPreviewModalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly assessmentCentralService: AssessmentCentralService,
    private readonly loggingService: LoggingService,
    private readonly authService: AuthService
  ) {}

  ngOnInit() {
    this.authService.tagsEnabled.subscribe((res: any) => {
      this.showTags = res;
    });
    this.template = this.data.dataKey;
    this.tagsList = this.data.tagsList?.map((item: any) => {
      if (item?.id) {
        return item.id;
      } else {
        return item;
      }
    });
    this.description = this.template.description;
    this.applicability = this.template.applicability;
    this.assessmentScoringScale = this.template.assessment_scoring_scale;
    this.categoriesList = this.data.categoriesList;
    if (!this.categoriesList && this.template.category) {
      this.assessmentCentralService
        .getCategoriesList("assessment_category")
        .subscribe((resp) => {
          this.categoriesList = resp;
          this.categoryObject = this.categoriesList?.find(
            (item: any) => item?.id === this.template.category
          );
          if (this.categoryObject) {
            this.category = this.categoryObject.display_name;
          }
        });
    } else if (this.categoriesList) {
      this.categoryObject = this.categoriesList.find(
        (item: any) => item?.id === this.template.category
      );
      if (this.categoryObject) {
        this.category = this.categoryObject.display_name;
      }
    } else {
      this.loggingService.warn("Unexpected value");
    }
    this.template.questions.forEach((element: any) => {
      const i = this.questions.findIndex(
        (e: any) => e.group === element.group_name
      );
      if (i > -1) {
        this.questions[i].questions.push(element);
      } else {
        this.questions.push({
          group: element.group_name,
          questions: [element],
        });
      }
    });
  }

  closeModal(bool = false): void {
    this.dialogRef.close(bool);
  }
}

@Component({
  selector: "app-assessment-templates",
  templateUrl: "./assessment-templates.component.html",
  styleUrls: ["./assessment-templates.component.scss"],
})
export class AssessmentTemplatesComponent implements OnInit, OnDestroy {
  templates: any = [];
  filteredTemplate: any = [];
  unCategorizedTemplates: any = [];
  file: any;
  categoriesList: any = [];
  readMode: any = false;
  subscription: any;
  filterObj: any;
  tagFilterId: any;
  templatesLoaded = false;
  fromIpdb: any = "0";
  filterAppliedStatus = false;
  isDataLoaded = false;
  filterCfg: any;
  private readonly unsubscribe$ = new Subject<void>();
  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly assessmentCentralService: AssessmentCentralService,
    private readonly spinner: NgxSpinnerService,
    private readonly messageService: MessageService,
    private readonly toastrService: ToastrService,
    private readonly tagService: TagsService,
    private readonly route: ActivatedRoute,
    private readonly filterService: FilterService,
    private readonly loggingService: LoggingService,
    private readonly breadCrumbService: BreadcrumbService,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.getCategories();
    this.initFilterCfg();
    this.messageService.setGnews({
      pagename: "Assessment Central - Templates",
    });
    this.route.queryParamMap.subscribe((params) => {
      this.readMode = params.get("readMode") === "1";
      this.fromIpdb = params.get("ipdb");
    });
    this.filterService.filterValues
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any) => {
        this.filterObj = res;
        let awl = false;
        this.filterObj?.inputs?.forEach((element: any) => {
          if (hasValue(element.value, this.loggingService)) {
            awl = true;
          }
        });
        if (awl) {
          this.search();
        } else {
          this.getTemplates();
        }
      });
    const breadCrumb = [
      { label: "Confluence", url: "/confluence" },
      {
        label: "Assessments & Surveys",
        url: "/strategicRelevance/assessment-central",
      },
      { label: "Assessment Library", url: "assessment-templates" },
    ];
    this.breadCrumbService.setBreadcrumb(breadCrumb);
  }

  initFilterCfg() {
    const searchableSelect = "searchable-select";
    forkJoin({
      names: this.getNames(),
      owners: this.getOwnersList(),
      tags: this.getTag(),
    }).subscribe(({ names, owners, tags }) => {
      this.filterCfg = {
        inputs: [
          {
            field: "category",
            type: searchableSelect,
            placeholder: "Search Category...",
            options: this.categoriesList,
            value: null,
          },
          {
            field: "name",
            type: searchableSelect,
            placeholder: "Search Name...",
            options: names,
            value: null,
          },
          {
            field: "applicability",
            type: "input",
            placeholder: "Search Applicability...",
            value: null,
          },
          {
            field: "tag",
            type: "searchable-multiselect",
            placeholder: "Search Tag...",
            options: tags,
            value: [],
          },
          {
            field: "owner",
            type: searchableSelect,
            placeholder: "Search Owner...",
            options: owners,
            value: null,
          },
        ],
      };
      this.filterService.filterCfgs.next(this.filterCfg);
    });
  }

  getCategories() {
    this.assessmentCentralService
      .getCategoriesList("assessment_category")
      .subscribe((resp) => {
        this.categoriesList = resp;
      });
  }

  getTag() {
    return this.filterService.getTag().pipe(
      map((res: any) => {
        return res.map((tag: any) => ({
          ...tag,
          name: tag.tag_name,
        }));
      })
    );
  }

  getOwnersList() {
    return this.assessmentCentralService.getOwnersList().pipe(
      map((res: any) => {
        return res.map((owner: any) => ({
          ...owner,
          display_name: `${owner.first_name} ${owner.last_name}`,
        }));
      })
    );
  }

  getNames() {
    return this.assessmentCentralService
      .getAssesmentCentralNamesList("assignment_template")
      .pipe(
        map((res: any) => {
          return res.map((name: any) => ({
            ...name,
            display_name: name.name,
            id: name.name,
          }));
        })
      );
  }

  navigateToLibrary() {
    this.router.navigateByUrl(
      `strategicRelevance/assessment-central/assessment-templates?readMode=0`
    );
  }

  getTemplates() {
    this.filterAppliedStatus = false;
    this.templatesLoaded = false;
    this.assessmentCentralService.getTemplates().subscribe((res: any) => {
      res.forEach((category: any) => {
        category.templates.forEach((element: any) => {
          element.color = this.getGMapRandomColor();
        });
      });
      this.templates = res;
      this.filteredTemplate = res;
      this.unCategorizedTemplates =
        this.filteredTemplate?.reduce(
          (acc: any, category: any) => [...acc, ...category.templates],
          []
        ) || [];
      this.templatesLoaded = true;
      this.isDataLoaded = true;
    });
  }

  templateDownload() {
    window.open(
      "/assets/template/assessment_questionaire_template.xlsx",
      "_blank"
    );
  }

  previewAssessment(template: any, tags: any, categoriesList: any) {
    const dialog = this.dialog.open(AssessmentPreviewModalDialogComponent, {
      width: "50vw",
      disableClose: true,
      autoFocus: false,
      data: {
        dataKey: template,
        tagsList: tags,
        categoriesList,
      },
    });
    dialog.afterClosed().subscribe((data) => {
      if (data) {
        this.proceed(template);
      }
    });
  }

  proceed(template: any) {
    const dialog = this.dialog.open(AssessmentCreateModalDialogComponent, {
      width: "500px",
      autoFocus: false,
      disableClose: true,
      data: { template_id: template.id, template_name: template.name },
    });
    dialog.afterClosed();
  }

  async addTags(id: any, selectedTags: any) {
    if (selectedTags?.length > 0) {
      await selectedTags.forEach(async (element: any) => {
        const pst = {
          entity_id: id,
          entity_type: "assessment_template",
          tag_id: element.id,
        };
        this.tagService.magTag(pst).subscribe(() => {});
      });
    }
  }

  onFilechange(event: any) {
    const selectedFile = event.target.files[0];
    this.commonService.validateFileSize(selectedFile).subscribe((isValid) => {
      if (!isValid) {
        this.toastrService.error("Please upload less than 50MB");
        return;
      }
      this.file = selectedFile;
      const dialog = this.dialog.open(
        AssessmentCreateTemplateModalDialogComponent,
        {
          width: "600px",
          autoFocus: false,
          disableClose: true,
          data: {
            uploadedFile: this.file,
          },
        }
      );
      dialog.afterClosed().subscribe((data) => {
        if (data) {
          const formData = new FormData();
          formData.append("file", this.file);
          formData.append("name", data.name);
          formData.append("description", data.description);
          formData.append("applicability", data.applicability);
          formData.append("scoring_methodology", "");
          formData.append(
            "assessment_scoring_scale",
            data.assessment_scoring_scale
          );
          formData.append("category", data.category);
          this.assessmentCentralService
            .uploadFile(formData)
            .subscribe((response: any) => {
              this.spinner.hide();
              if (response) {
                this.file = [];
                this.addTags(response.id, data.selectedTags);
                this.previewAssessment(
                  response,
                  data.selectedTags,
                  this.categoriesList
                );
                this.getTemplates();
              }
            });
        }
        event.target.value = "";
      });
    });
  }

  getGMapRandomColor() {
    const maxHue = 360;
    return `hsla(${Math.floor(Math.random() * maxHue)}, 50%, 25%, 90%)`;
  }

  getFilterQueryParams() {
    let qprm = "search=";
    let isFirst = true;
    this.filterObj?.inputs.forEach((element: any) => {
      const value = element.value;
      if (value) {
        if (isFirst) {
          isFirst = false;
        } else {
          qprm += ",";
        }
        if (element.field === "owner") {
          qprm += `created_by:${value.id}`;
        } else if (element.field === "category") {
          qprm += `category:${value.id}`;
        } else if (element.field === "name") {
          qprm += `name:${value.id}`;
        } else if (element.field === "applicability") {
          qprm += `applicability:${value}`;
        } else if (element.field === "tag") {
          let tags: any = [];
          value.forEach((tag: any) => {
            tags.push(tag.id);
          });
          tags = tags.join("|");
          qprm += `tags:${tags}`;
        } else {
          this.loggingService.warn("Unexpected value");
        }
      }
    });
    return qprm;
  }

  search() {
    this.isDataLoaded = false;
    const qParam = this.getFilterQueryParams();
    this.filterAppliedStatus = true;
    this.assessmentCentralService
      .getFilteredAssessmentTemplates(qParam)
      .subscribe((res: any) => {
        res.forEach((category: any) => {
          category.templates.forEach((element: any) => {
            element.color = this.getGMapRandomColor();
          });
        });
        this.templates = res;
        this.filteredTemplate = res;
        this.unCategorizedTemplates =
          this.filteredTemplate?.reduce(
            (acc: any, category: any) => [...acc, ...category.templates],
            []
          ) || [];
        this.isDataLoaded = true;
      });
  }

  deleteTemplate(id: any, name: string) {
    const dialogRef = this.dialog.open(DeletePopupComponent, {
      data: name,
      disableClose: true,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.assessmentCentralService.deleteTemplate(id).subscribe(() => {
          this.toastrService.success("Templated deleted successfully");
          this.getTemplates();
        });
      }
    });
  }

  blankPlan() {
    this.proceed(null);
  }

  navigateToAssessmentCentral() {
    this.router.navigateByUrl(
      "strategicRelevance/assessment-central?tab=assessment"
    );
  }

  navigateToIpdb() {
    this.router.navigateByUrl("/prism/ipdb");
  }

  downloadTemplate(templateId: any, name: any) {
    this.assessmentCentralService.downloadTemplate(templateId).subscribe(
      (res: any) => {
        let downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(res);
        downloadLink.setAttribute("download", name);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        this.toastrService.success("Template downloaded successfully");
        this.spinner.hide();
      },
      (error: any) => {
        this.spinner.hide();
        throw error;
      }
    );
  }

  ngOnDestroy() {
    this.filterService.filterCfgs.next({});
    this.filterService.setFilterValue("");
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
